import React, { useState } from 'react'
import { Card } from 'primereact/card'
import { Chart } from 'primereact/chart'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { TabView, TabPanel } from 'primereact/tabview'
import { ProgressBar } from 'primereact/progressbar'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import { Button } from 'primereact/button'
import { Badge } from 'primereact/badge'
import { Tag } from 'primereact/tag'
import PageHeader from '../../../shared/PageHeader'
import CheckPermission from '../../../hooks/CheckPermission'

const SupplyChainDashboard = () => {
  const { hasPermission } = CheckPermission()
  const [activeTab, setActiveTab] = useState(0)
  const [dateRange, setDateRange] = useState(null)
  const [selectedSupplier, setSelectedSupplier] = useState(null)

  if (!hasPermission('inventory', 'get')) {
    return <div className='p-4 text-center text-red-500 font-semibold'>Access Denied</div>
  }

  // Chart data
  const procurementChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Purchase Orders',
        data: [12, 19, 3, 5, 2, 3],
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.7
      },
      {
        label: 'Received Orders',
        data: [10, 15, 2, 4, 2, 2],
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.7
      }
    ]
  }

  const inventoryChartData = {
    labels: ['Electronics', 'Furniture', 'Office', 'Raw Materials', 'Finished'],
    datasets: [
      {
        data: [300, 150, 200, 450, 320],
        backgroundColor: [
          'rgba(99, 102, 241, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(239, 68, 68, 0.7)',
          'rgba(139, 92, 246, 0.7)'
        ],
        borderColor: [
          'rgba(99, 102, 241, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(139, 92, 246, 1)'
        ],
        borderWidth: 1
      }
    ]
  }

  // Table data
  const procurementOrders = [
    {
      id: 'PO-1001',
      supplier: 'ABC Corp',
      date: '2023-05-01',
      amount: 4500,
      status: 'Delivered'
    },
    {
      id: 'PO-1002',
      supplier: 'XYZ Ltd',
      date: '2023-05-05',
      amount: 3200,
      status: 'In Transit'
    },
    {
      id: 'PO-1003',
      supplier: 'Global Suppliers',
      date: '2023-05-10',
      amount: 7800,
      status: 'Processing'
    },
    {
      id: 'PO-1004',
      supplier: 'Best Materials',
      date: '2023-05-12',
      amount: 2100,
      status: 'Processing'
    },
    {
      id: 'PO-1005',
      supplier: 'Quality Goods',
      date: '2023-05-15',
      amount: 5400,
      status: 'Approved'
    }
  ]

  const inventoryItems = [
    {
      sku: 'INV-1001',
      name: 'Laptop',
      stock: 120,
      threshold: 20,
      status: 'In Stock'
    },
    {
      sku: 'INV-1002',
      name: 'Office Chair',
      stock: 45,
      threshold: 10,
      status: 'In Stock'
    },
    {
      sku: 'INV-1003',
      name: 'Printer Paper',
      stock: 15,
      threshold: 25,
      status: 'Low Stock'
    },
    {
      sku: 'INV-1004',
      name: 'Steel Sheets',
      stock: 320,
      threshold: 100,
      status: 'In Stock'
    },
    {
      sku: 'INV-1005',
      name: 'Product A',
      stock: 85,
      threshold: 30,
      status: 'In Stock'
    }
  ]

  const suppliers = [
    { name: 'ABC Corp', rating: 'A', performance: 92 },
    { name: 'XYZ Ltd', rating: 'B', performance: 75 },
    { name: 'Global Suppliers', rating: 'A', performance: 88 },
    { name: 'Best Materials', rating: 'C', performance: 62 },
    { name: 'Quality Goods', rating: 'B', performance: 78 }
  ]

  // Status templates
  const statusBodyTemplate = rowData => {
    const statusMap = {
      Delivered: 'success',
      'In Transit': 'warning',
      Processing: 'info',
      Approved: null
    }
    return <Tag value={rowData.status} severity={statusMap[rowData.status]} />
  }

  const inventoryStatusTemplate = rowData => {
    const severity = rowData.stock > rowData.threshold ? 'success' : 'danger'
    return <Tag value={rowData.status} severity={severity} />
  }

  const supplierRatingTemplate = rowData => {
    const ratingMap = {
      A: 'bg-indigo-100 text-indigo-800',
      B: 'bg-green-100 text-green-800',
      C: 'bg-amber-100 text-amber-800'
    }
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          ratingMap[rowData.rating]
        }`}
      >
        {rowData.rating}
      </span>
    )
  }

  return (
    // <div className='main-page-container !overflow-y-auto scrollbar'>
    //   <div className='flex justify-between items-center mb-4'>
    //     <div className='flex space-x-2'>
    //       <Calendar
    //         value={dateRange}
    //         onChange={e => setDateRange(e.value)}
    //         selectionMode='range'
    //         readOnlyInput
    //         placeholder='Select date range'
    //         className='w-60 text-sm'
    //         showIcon
    //       />
    //       <Dropdown
    //         value={selectedSupplier}
    //         onChange={e => setSelectedSupplier(e.value)}
    //         options={suppliers}
    //         optionLabel='name'
    //         placeholder='All Suppliers'
    //         className='w-48 text-sm'
    //         showClear
    //       />
    //     </div>
    //   </div>

    //   {/* Summary Cards */}
    //   <div className='grid grid-cols-1 md:grid-cols-4 gap-3 mb-4'>
    //     <Card className='shadow-sm border-0'>
    //       <div className='p-3'>
    //         <div className='flex justify-between items-start'>
    //           <div>
    //             <p className='text-sm font-medium text-gray-500'>
    //               Total Orders
    //             </p>
    //             <p className='text-2xl font-semibold'>24</p>
    //           </div>
    //           <div className='p-2 rounded-lg bg-indigo-50'>
    //             <i className='pi pi-shopping-cart text-indigo-600'></i>
    //           </div>
    //         </div>
    //         <ProgressBar value={80} className='mt-2 h-1' showValue={false} />
    //       </div>
    //     </Card>

    //     <Card className='shadow-sm border-0'>
    //       <div className='p-3'>
    //         <div className='flex justify-between items-start'>
    //           <div>
    //             <p className='text-sm font-medium text-gray-500'>
    //               Pending Orders
    //             </p>
    //             <p className='text-2xl font-semibold'>5</p>
    //           </div>
    //           <div className='p-2 rounded-lg bg-amber-50'>
    //             <i className='pi pi-clock text-amber-600'></i>
    //           </div>
    //         </div>
    //         <ProgressBar
    //           value={20}
    //           className='mt-2 h-1'
    //           showValue={false}
    //           color='amber'
    //         />
    //       </div>
    //     </Card>

    //     <Card className='shadow-sm border-0'>
    //       <div className='p-3'>
    //         <div className='flex justify-between items-start'>
    //           <div>
    //             <p className='text-sm font-medium text-gray-500'>
    //               Inventory Items
    //             </p>
    //             <p className='text-2xl font-semibold'>1,245</p>
    //           </div>
    //           <div className='p-2 rounded-lg bg-green-50'>
    //             <i className='pi pi-box text-green-600'></i>
    //           </div>
    //         </div>
    //         <ProgressBar
    //           value={65}
    //           className='mt-2 h-1'
    //           showValue={false}
    //           color='green'
    //         />
    //       </div>
    //     </Card>

    //     <Card className='shadow-sm border-0'>
    //       <div className='p-3'>
    //         <div className='flex justify-between items-start'>
    //           <div>
    //             <p className='text-sm font-medium text-gray-500'>Low Stock</p>
    //             <p className='text-2xl font-semibold'>18</p>
    //           </div>
    //           <div className='p-2 rounded-lg bg-red-50'>
    //             <i className='pi pi-exclamation-triangle text-red-600'></i>
    //           </div>
    //         </div>
    //         <ProgressBar
    //           value={15}
    //           className='mt-2 h-1'
    //           showValue={false}
    //           color='red'
    //         />
    //       </div>
    //     </Card>
    //   </div>

    //   {/* Main Content */}
    //   <TabView
    //     activeIndex={activeTab}
    //     onTabChange={e => setActiveTab(e.index)}
    //     className='text-sm'
    //   >
    //     <TabPanel header='Procurement' className='p-0'>
    //       <div className='grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4'>
    //         <div className='lg:col-span-2'>
    //           <Card className='shadow-sm border-0 h-full'>
    //             <div className='p-3'>
    //               <h3 className='font-medium text-gray-700 mb-3'>
    //                 Purchase Orders Trend
    //               </h3>
    //               <Chart
    //                 type='bar'
    //                 data={procurementChartData}
    //                 className='w-full h-64'
    //               />
    //             </div>
    //           </Card>
    //         </div>
    //         <div>
    //           <Card className='shadow-sm border-0 h-full'>
    //             <div className='p-3'>
    //               <h3 className='font-medium text-gray-700 mb-3'>
    //                 Supplier Performance
    //               </h3>
    //               <div className='space-y-3'>
    //                 {suppliers.map((supplier, index) => (
    //                   <div key={index} className='flex items-center space-x-3'>
    //                     <div className='flex-1 min-w-0'>
    //                       <p className='text-sm font-medium truncate'>
    //                         {supplier.name}
    //                       </p>
    //                       <ProgressBar
    //                         value={supplier.performance}
    //                         showValue={false}
    //                         className='h-1.5 mt-1'
    //                         severity={
    //                           supplier.rating === 'A'
    //                             ? 'success'
    //                             : supplier.rating === 'B'
    //                             ? 'info'
    //                             : 'warning'
    //                         }
    //                       />
    //                     </div>
    //                     {supplierRatingTemplate(supplier)}
    //                   </div>
    //                 ))}
    //               </div>
    //             </div>
    //           </Card>
    //         </div>
    //       </div>

    //       <Card className='shadow-sm border-0'>
    //         <div className='p-3'>
    //           <div className='flex justify-between items-center mb-3'>
    //             <h3 className='font-medium text-gray-700'>
    //               Recent Purchase Orders
    //             </h3>
    //             <Button label='New Order' icon='pi pi-plus' size='small' />
    //           </div>
    //           <DataTable
    //             value={procurementOrders}
    //             paginator
    //             rows={5}
    //             rowsPerPageOptions={[5, 10, 25]}
    //             className='p-datatable-sm'
    //             emptyMessage='No orders found'
    //           >
    //             <Column
    //               field='id'
    //               header='ID'
    //               sortable
    //               style={{ width: '100px' }}
    //             />
    //             <Column field='supplier' header='Supplier' sortable />
    //             <Column
    //               field='date'
    //               header='Date'
    //               sortable
    //               style={{ width: '120px' }}
    //             />
    //             <Column
    //               field='amount'
    //               header='Amount'
    //               sortable
    //               style={{ width: '120px' }}
    //               body={rowData => `$${rowData.amount.toLocaleString()}`}
    //             />
    //             <Column
    //               field='status'
    //               header='Status'
    //               body={statusBodyTemplate}
    //               sortable
    //               style={{ width: '120px' }}
    //             />
    //             <Column
    //               body={() => (
    //                 <Button
    //                   icon='pi pi-ellipsis-h'
    //                   className='p-button-text p-button-sm'
    //                 />
    //               )}
    //               style={{ width: '50px' }}
    //             />
    //           </DataTable>
    //         </div>
    //       </Card>
    //     </TabPanel>

    //     <TabPanel header='Inventory' className='p-0'>
    //       <div className='grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4'>
    //         <div className='lg:col-span-2'>
    //           <Card className='shadow-sm border-0 h-full'>
    //             <div className='p-3'>
    //               <h3 className='font-medium text-gray-700 mb-3'>
    //                 Inventory Distribution
    //               </h3>
    //               <Chart
    //                 type='doughnut'
    //                 data={inventoryChartData}
    //                 className='w-full h-64'
    //               />
    //             </div>
    //           </Card>
    //         </div>
    //         <div>
    //           <Card className='shadow-sm border-0 h-full'>
    //             <div className='p-3'>
    //               <h3 className='font-medium text-gray-700 mb-3'>
    //                 Stock Alerts
    //               </h3>
    //               <div className='space-y-2'>
    //                 {inventoryItems
    //                   .filter(item => item.status === 'Low Stock')
    //                   .map((item, index) => (
    //                     <div
    //                       key={index}
    //                       className='p-2 bg-red-50 rounded flex justify-between items-center'
    //                     >
    //                       <div className='truncate'>
    //                         <p className='text-sm font-medium truncate'>
    //                           {item.name}
    //                         </p>
    //                         <p className='text-xs text-gray-600'>
    //                           Stock: {item.stock} / Threshold: {item.threshold}
    //                         </p>
    //                       </div>
    //                       <Button
    //                         icon='pi pi-shopping-cart'
    //                         className='p-button-sm p-button-text p-button-danger'
    //                       />
    //                     </div>
    //                   ))}
    //                 {inventoryItems.filter(item => item.status === 'Low Stock')
    //                   .length === 0 && (
    //                   <p className='text-sm text-gray-500 text-center py-4'>
    //                     No stock alerts
    //                   </p>
    //                 )}
    //               </div>
    //             </div>
    //           </Card>
    //         </div>
    //       </div>

    //       <Card className='shadow-sm border-0'>
    //         <div className='p-3'>
    //           <div className='flex justify-between items-center mb-3'>
    //             <h3 className='font-medium text-gray-700'>Inventory Items</h3>
    //             <Button label='Add Item' icon='pi pi-plus' size='small' />
    //           </div>
    //           <DataTable
    //             value={inventoryItems}
    //             paginator
    //             rows={5}
    //             rowsPerPageOptions={[5, 10, 25]}
    //             className='p-datatable-sm'
    //             emptyMessage='No inventory items found'
    //           >
    //             <Column
    //               field='sku'
    //               header='SKU'
    //               sortable
    //               style={{ width: '100px' }}
    //             />
    //             <Column field='name' header='Item' sortable />
    //             <Column
    //               field='stock'
    //               header='Stock'
    //               sortable
    //               style={{ width: '100px' }}
    //             />
    //             <Column
    //               field='threshold'
    //               header='Threshold'
    //               sortable
    //               style={{ width: '100px' }}
    //             />
    //             <Column
    //               field='status'
    //               header='Status'
    //               body={inventoryStatusTemplate}
    //               sortable
    //               style={{ width: '100px' }}
    //             />
    //             <Column
    //               body={() => (
    //                 <div className='flex space-x-1'>
    //                   <Button
    //                     icon='pi pi-pencil'
    //                     className='p-button-text p-button-sm p-button-primary'
    //                   />
    //                   <Button
    //                     icon='pi pi-trash'
    //                     className='p-button-text p-button-sm p-button-danger'
    //                   />
    //                 </div>
    //               )}
    //               style={{ width: '80px' }}
    //             />
    //           </DataTable>
    //         </div>
    //       </Card>
    //     </TabPanel>
    //   </TabView>
    // </div>
    <div>Dashboard</div>
  )
}

export default SupplyChainDashboard
